/**
 * Choosing the print printer.
 *
 * Order: the app printer chosen on this computer (if still installed) ->
 * system default printer -> the only printer -> ask the user
 * (PrinterPickerHost dialog). The app choice wins over the system default.
 */

import { LocalStorageKeys } from "./constants";

export const SAVED_PRINTER_KEY = LocalStorageKeys.APP_PRINTER;
/** Last system default QZ reported, so the status bar can show it without QZ. */
export const SYSTEM_PRINTER_KEY = LocalStorageKeys.SYSTEM_PRINTER;

export type PrinterChoice = { printer: string | null; remember: boolean };
export type PickMode = "print" | "settings";
export type PickRequest = {
	printers: string[];
	mode: PickMode;
	systemDefault: string | null;
	current: string | null;
	/** Settings mode: QZ Tray could not be reached, so the list is empty. */
	unavailable?: boolean;
	/** Settings mode: re-list printers (the dialog's refresh button). */
	reload?: () => Promise<{ printers: string[]; systemDefault: string | null } | null>;
	resolve: (choice: PrinterChoice) => void;
};
type QzPrinters = {
	getDefault: () => Promise<unknown>;
	find: (query?: string) => Promise<unknown>;
};

let host: ((request: PickRequest) => void) | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

/** Subscribe to app/system printer changes; returns an unsubscribe callback. */
export const subscribePrinter = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

/** Mounted dialog registers here; returns an unregister callback. */
export const registerPrinterPicker = (handler: (request: PickRequest) => void) => {
	host = handler;
	return () => {
		if (host === handler) host = null;
	};
};

type PickOptions = Omit<PickRequest, "printers" | "resolve" | "mode"> & { mode?: PickMode };

/** Ask the user to pick one of `printers`. Resolves printer=null when cancelled or no picker is mounted. */
export const pickPrinter = (printers: string[], options: Partial<PickOptions> = {}): Promise<PrinterChoice> =>
	new Promise((resolve) => {
		if (!host) {
			resolve({ printer: null, remember: false });
			return;
		}
		host({
			printers,
			mode: options.mode ?? "print",
			systemDefault: options.systemDefault ?? loadSystemPrinter(),
			current: options.current ?? loadSavedPrinter(),
			unavailable: options.unavailable,
			reload: options.reload,
			resolve,
		});
	});

const read = (key: string): string | null => {
	try {
		return localStorage.getItem(key) || null;
	} catch {
		return null;
	}
};

const write = (key: string, value: string | null) => {
	try {
		if (read(key) === value) return;
		if (value) localStorage.setItem(key, value);
		else localStorage.removeItem(key);
	} catch {
		// Storage unavailable (private mode): the choice just isn't remembered.
		return;
	}
	notify();
};

export const loadSavedPrinter = (): string | null => read(SAVED_PRINTER_KEY);
export const saveSavedPrinter = (printer: string | null) => write(SAVED_PRINTER_KEY, printer);
export const loadSystemPrinter = (): string | null => read(SYSTEM_PRINTER_KEY);
export const saveSystemPrinter = (printer: string | null) => write(SYSTEM_PRINTER_KEY, printer);

/** Printer a print would use without asking, and where it comes from. */
export const effectivePrinter = (): { printer: string | null; source: "app" | "system" | null } => {
	const app = loadSavedPrinter();
	if (app) return { printer: app, source: "app" };
	const system = loadSystemPrinter();
	if (system) return { printer: system, source: "system" };
	return { printer: null, source: null };
};

const asName = (value: unknown): string | null => {
	if (typeof value === "string" && value.trim()) return value;
	if (value && typeof value === "object" && typeof (value as { name?: unknown }).name === "string") {
		return (value as { name: string }).name || null;
	}
	return null;
};

/** Installed printers and the system default, as QZ reports them. Refreshes the stored system default. */
export async function listPrinters(printers: QzPrinters): Promise<{ printers: string[]; systemDefault: string | null }> {
	const [defaultValue, found] = await Promise.all([
		printers.getDefault().catch(() => null),
		printers.find().catch(() => []),
	]);
	const list = Array.from(
		new Set((Array.isArray(found) ? found : [found]).map(asName).filter((name): name is string => !!name)),
	);
	const systemDefault = asName(defaultValue);
	saveSystemPrinter(systemDefault);
	return { printers: list, systemDefault };
}

export type PrinterResolution =
	| { printer: string; reason: "saved" | "default" | "only" | "picked" }
	| { printer: null; reason: "none" | "cancelled" };

/** Resolve the printer to use; printer is null when none is installed or the user cancels. */
export async function resolvePrinter(printers: QzPrinters, pick: (list: string[]) => Promise<PrinterChoice> = pickPrinter): Promise<PrinterResolution> {
	const { printers: list, systemDefault } = await listPrinters(printers);

	const saved = loadSavedPrinter();
	// An empty list may just be a failed lookup: keep trusting the app printer then.
	if (saved && (list.includes(saved) || list.length === 0)) return { printer: saved, reason: "saved" };
	if (saved) saveSavedPrinter(null); // app printer was removed from this machine

	if (systemDefault) return { printer: systemDefault, reason: "default" };
	if (list.length === 0) return { printer: null, reason: "none" };
	if (list.length === 1) return { printer: list[0], reason: "only" };

	const choice = await pick(list);
	if (!choice.printer) return { printer: null, reason: "cancelled" };
	if (choice.remember) saveSavedPrinter(choice.printer);
	return { printer: choice.printer, reason: "picked" };
}
